import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/store/authStore';
import * as authService from '@/api/services/auth.service';
import type { LoginRequest, RegisterRequest, User } from '@/api/types/auth.types';

/** Query key constants */
const AUTH_KEYS = {
  me: ['auth', 'me'] as const,
};

/**
 * Fetch the current user profile.
 * Only runs when the user is authenticated (has an access token).
 */
export function useMe() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);

  return useQuery({
    queryKey: AUTH_KEYS.me,
    queryFn: async () => {
      const user = await authService.getMe();
      setUser(user);
      return user;
    },
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/** Login mutation. Sets tokens + user in auth store and navigates on success. */
export function useLogin() {
  const { setTokens, setUser } = useAuthStore.getState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (credentials: LoginRequest) =>
      authService.login(credentials),
    onSuccess: (data) => {
      setTokens(data.access, data.refresh);
      setUser(data.user);
      queryClient.setQueryData(AUTH_KEYS.me, data.user);
      navigate('/', { replace: true });
    },
    onError: () => {
      toast.error(t('auth.loginError'));
    },
  });
}

/** Register mutation. Sets tokens + user in auth store and navigates on success. */
export function useRegister() {
  const { setTokens, setUser } = useAuthStore.getState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: RegisterRequest) => authService.register(payload),
    onSuccess: (data) => {
      setTokens(data.access, data.refresh);
      setUser(data.user);
      queryClient.setQueryData(AUTH_KEYS.me, data.user);
      navigate('/', { replace: true });
    },
    onError: () => {
      toast.error(t('auth.registerError'));
    },
  });
}

/** Logout mutation. Blacklists token on server, then clears local state. */
export function useLogout() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async () => {
      const { refreshToken, clearAuth } = useAuthStore.getState();
      if (refreshToken) {
        try {
          await authService.logout(refreshToken);
        } catch {
          // Server-side logout failure is non-critical; still clear locally.
        }
      }
      clearAuth();
      queryClient.clear();
    },
    onSuccess: () => {
      window.location.href = '/login';
    },
    onError: () => {
      useAuthStore.getState().clearAuth();
      queryClient.clear();
      toast.error(t('auth.logoutError'));
      window.location.href = '/login';
    },
  });
}

/** Update the current user's profile. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: Partial<User>) => authService.updateMe(payload),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(AUTH_KEYS.me, user);
      toast.success(t('auth.profileUpdated'));
    },
    onError: () => {
      toast.error(t('auth.profileUpdateError'));
    },
  });
}

/** Request an OTP verification call. */
export function useRequestOtp() {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (phone: string) => authService.requestOtp(phone),
    onSuccess: () => {
      toast.success(t('auth.otpRequestSuccess'));
    },
    onError: () => {
      toast.error(t('auth.otpRequestError'));
    },
  });
}

/** Verify OTP code and log in. */
export function useVerifyOtp() {
  const { setTokens, setUser } = useAuthStore.getState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId, code }: { userId: string; code: string }) =>
      authService.verifyOtp(userId, code),
    onSuccess: (data) => {
      setTokens(data.access, data.refresh);
      setUser(data.user);
      queryClient.setQueryData(AUTH_KEYS.me, data.user);
      navigate('/', { replace: true });
    },
    onError: () => {
      toast.error(t('auth.otpVerifyError'));
    },
  });
}

/** Google SSO login. */
export function useGoogleLogin() {
  const { setTokens, setUser } = useAuthStore.getState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (credential: string) => authService.googleLogin(credential),
    onSuccess: (data) => {
      setTokens(data.access, data.refresh);
      setUser(data.user);
      queryClient.setQueryData(AUTH_KEYS.me, data.user);
      navigate('/', { replace: true });
    },
    onError: () => {
      toast.error(t('auth.googleLoginError'));
    },
  });
}
